import { OfflineReportSubmissionQueueProcessor } from '@/screens/ReportCreation/components/OfflineReportSubmissionQueueProcessor';
import { useGetConfiguration } from '@api/WhisApi/configuration/configuration.queries';
import { AuthGate } from '@components/AuthGate/AuthGate';
import { ReferentialsPrefetcher } from '@components/Referentials/ReferentialsPrefetcher';
import { UserPrefetcher } from '@components/User/UserPrefetcher';
import { useIsWeb } from '@hooks/useIsWeb';
import FatalErrorScreen from '@screens/FatalErrorScreen/FatalErrorScreen';
import { AuthProvider } from '@store/auth/auth.store';
import { initI18n } from '@store/i18n';
import Constants from 'expo-constants';
import { useFonts } from 'expo-font';
import { SplashScreen, Stack } from 'expo-router';
import { Head } from 'expo-router/build/head';
import { useEffect, useState } from 'react';
import { appConfig as tamaguiConfig } from '../../tamagui.config';

export default function AppBootstrap(): React.ReactNode {
  const isWeb = useIsWeb();

  const { data: configuration, isPending } = useGetConfiguration();

  const [fontsLoaded] = useFonts({
    SohneRegular: require('@assets/fonts/Sohne-Buch.otf'),
    SohneMedium: require('@assets/fonts/Sohne-Kraftig.otf'),
    SohneBold: require('@assets/fonts/Sohne-Halbfett.otf'),
    SohneSchmalRegular: require('@assets/fonts/SohneSchmal-Kraftig.otf'),
    SohneSchmalMedium: require('@assets/fonts/SohneSchmal-Halbfett.otf'),
    SohneSchmalBold: require('@assets/fonts/SohneSchmal-Dreiviertelfett.otf'),
  });

  const [i18nInitialized, setI18nInitialized] = useState(false);

  const initializeI18n = async (): Promise<void> => {
    await initI18n();
    setI18nInitialized(true);
  };

  // Initialize i18n asynchronously
  useEffect(() => {
    initializeI18n();
  }, []);

  useEffect(() => {
    if (!fontsLoaded || isPending) {
      return;
    }

    void SplashScreen.hideAsync();
  }, [fontsLoaded, isPending]);

  // Block rendering until fonts, i18n and configuration are loaded to avoid fallback system fonts and untranslated content
  // Returns null during loading, keeping splash screen visible
  if (!fontsLoaded || !i18nInitialized || isPending) {
    return null;
  }

  if (!configuration) {
    return <FatalErrorScreen />;
  }

  return (
    <AuthProvider azureAdConfiguration={configuration.azureAdConfiguration}>
      <ReferentialsPrefetcher />
      <UserPrefetcher />
      <OfflineReportSubmissionQueueProcessor />
      <AuthGate>
        {isWeb && (
          <Head>
            <title>{Constants.expoConfig?.name ?? 'WildEpi'}</title>
          </Head>
        )}
        <Stack
          screenOptions={{
            headerShown: false,
            contentStyle: {
              backgroundColor: tamaguiConfig.themes.light.background.val,
            },
          }}
        />
      </AuthGate>
    </AuthProvider>
  );
}
